/**
 * The documented public read API (F27): the one list of endpoints that are
 * promised to scripts, notebooks and AI assistants, and the shapes of the
 * summary and citation endpoints. CORS, rate limits and the OpenAPI document
 * all read PUBLIC_API_ROUTES, so they cannot disagree (PAPI-001).
 *
 * Stability promise: within /api/v1, a documented field is never removed,
 * renamed or retyped; fields and endpoints may be added.
 *
 * Owner: public API (F27). Implements PAPI-001, PAPI-005 to PAPI-007.
 */
import type { Certainty } from '@bioterafi/evidence-model';

export const PUBLIC_API_PATHS = {
  /** GET -> 200 OpenAPI 3.1 document of the public read API (E134; PAPI-002). */
  openApi: '/api/v1/openapi.json',
  /** GET ?snapshotId= -> 200 EvidenceSummaryResponse (E135; PAPI-005). */
  evidenceSummary: (slug: string) => `/api/v1/evidence-pages/${encodeURIComponent(slug)}/summary`,
  /** GET ?snapshotId= -> 200 CitationResponse (E136; PAPI-006). */
  evidenceCitation: (slug: string) => `/api/v1/evidence-pages/${encodeURIComponent(slug)}/citation`,
  /** GET -> 200 ApiKeyListResponse; POST CreateApiKeyRequest -> 201 CreateApiKeyResponse (E137; PAPI-020, PAPI-023). Session only. */
  apiKeys: '/api/v1/me/api-keys',
  /** DELETE -> 204 (E138; PAPI-023). Session only. */
  apiKey: (id: string) => `/api/v1/me/api-keys/${encodeURIComponent(id)}`,
} as const;

/** One documented endpoint: its method, its path as an OpenAPI template, and what it returns. */
export interface PublicApiRoute {
  /** OpenAPI operationId. */
  id: string;
  method: 'GET';
  /** OpenAPI path template, e.g. /api/v1/evidence-pages/{slug}. */
  path: string;
  summary: string;
  /** Query parameters, all optional. */
  query?: { name: string; description: string; enum?: readonly string[] }[];
  /** api.md number. */
  ref: string;
}

/** The export kinds that are part of the public API (E49). */
export const PUBLIC_EXPORT_KINDS = ['csv', 'r-script', 'revman', 'prisma'] as const;

/** PAPI-001: exactly these, nothing else, are the documented public read API. */
export const PUBLIC_API_ROUTES: readonly PublicApiRoute[] = [
  { id: 'listEvidencePages', method: 'GET', path: '/api/v1/evidence-pages', summary: 'Evidence pages you can read: every shared page, and with an API key your own private pages.', ref: 'E08' },
  { id: 'getEvidencePage', method: 'GET', path: '/api/v1/evidence-pages/{slug}', summary: 'One evidence page: question, protocol, studies, values with their sources, and snapshots.', ref: 'E10' },
  { id: 'listSnapshots', method: 'GET', path: '/api/v1/evidence-pages/{slug}/snapshots', summary: 'The page’s snapshots, oldest first. Snapshots never change once written.', ref: 'E11' },
  {
    id: 'getEvidenceSummary',
    method: 'GET',
    path: '/api/v1/evidence-pages/{slug}/summary',
    summary: 'The pooled result of the primary outcome as stored with the latest snapshot (or the one named), its certainty and its provenance.',
    query: [{ name: 'snapshotId', description: 'A snapshot of this page; the latest when absent.' }],
    ref: 'E135',
  },
  {
    id: 'getCitation',
    method: 'GET',
    path: '/api/v1/evidence-pages/{slug}/citation',
    summary: 'How to cite the page and snapshot you used, as text, BibTeX and RIS.',
    query: [{ name: 'snapshotId', description: 'A snapshot of this page; the latest when absent.' }],
    ref: 'E136',
  },
  {
    id: 'getExport',
    method: 'GET',
    path: '/api/v1/evidence-pages/{slug}/exports/{kind}',
    summary: 'An export of the page: csv (data with sources), r-script (reproduces the analysis in R metafor), revman (RevMan Web import files) or prisma (flow diagram; SVG, or JSON with format=json).',
    query: [{ name: 'format', description: 'For prisma: svg (default) or json.', enum: ['svg', 'json'] }],
    ref: 'E49',
  },
  { id: 'getOpenApi', method: 'GET', path: '/api/v1/openapi.json', summary: 'This document.', ref: 'E134' },
];

/** PAPI-004: requests a minute per client address without an API key. */
export const PUBLIC_API_ANON_LIMIT_PER_MINUTE = 60;
/** PAPI-004: requests a minute per API key (F27 part 2). */
export const PUBLIC_API_KEY_LIMIT_PER_MINUTE = 600;

/** CONT-013, word for word: carried by the summary, the citation and every MCP result (PAPI-007). */
export const INTENDED_USE_STATEMENT =
  'Research and evidence synthesis tool. Not clinical advice. Do not use this output to make decisions about an individual patient’s care.';

export interface PooledResultSummary {
  measure: string;
  /** e.g. "Risk ratio". */
  measureName: string;
  /** Display scale (ratios as ratios, not logs). */
  estimate: number;
  ciLower: number;
  ciUpper: number;
  /** 0.95 for a 95% interval. */
  level: number;
  studies: number;
  /** As stored; null when the analysis does not estimate it. */
  i2: number | null;
  tau2: number | null;
  predictionInterval: { lower: number; upper: number } | null;
  model: string;
}

/** GET PUBLIC_API_PATHS.evidenceSummary (PAPI-005). Every number is copied from the stored snapshot. */
export interface EvidenceSummaryResponse {
  slug: string;
  title: string;
  url: string;
  protocolHash: string | null;
  snapshot: { id: string; createdAt: string } | null;
  primaryOutcome: { id: string; name: string } | null;
  /** Null when the page has no snapshot, or its snapshot could not be pooled (`reason`). */
  result: PooledResultSummary | null;
  reason: 'no_snapshot' | 'not_computed' | null;
  certainty: Certainty | null;
  intendedUse: string;
}

/** GET PUBLIC_API_PATHS.evidenceCitation (PAPI-006). */
export interface CitationResponse {
  text: string;
  bibtex: string;
  ris: string;
  retrievedOn: string;
  intendedUse: string;
}

/** PAPI-020: most active keys per user. */
export const API_KEY_LIMIT = 5;
export type ApiKeyExpiry = '90d' | '1y' | 'never';
export const API_KEY_EXPIRIES: readonly ApiKeyExpiry[] = ['90d', '1y', 'never'];

/** A key as listed: never the key itself. */
export interface ApiKeyListing {
  id: string;
  name: string;
  /** The key's last four characters, to recognise it. */
  lastFour: string;
  createdAt: string;
  expiresAt: string | null;
  /** YYYY-MM-DD, or null when never used. */
  lastUsedOn: string | null;
  /** Requests in the 30 days before now. */
  requests30d: number;
}

export interface ApiKeyListResponse {
  keys: ApiKeyListing[];
  limit: number;
}

export interface CreateApiKeyRequest {
  name: string;
  expiresIn: ApiKeyExpiry;
}

/** `key` is shown once and never again (PAPI-020). */
export interface CreateApiKeyResponse {
  key: string;
  apiKey: ApiKeyListing;
}


// ---------- MCP server (F27 part 3; ADR-0024) ----------

/** POST -> the read-only Model Context Protocol server, Streamable HTTP (E139; PAPI-040). */
export const MCP_PATH = '/mcp';

/** PAPI-041: exactly these tools, all read-only. */
export const MCP_TOOL_NAMES = ['search_evidence_pages', 'get_evidence_summary', 'get_changes', 'get_citation'] as const;
export type McpToolName = (typeof MCP_TOOL_NAMES)[number];

/** PAPI-043: where a tool result came from and what it is not for. Every tool result carries it. */
export interface McpProvenance {
  url: string;
  /** The snapshot the result came from; null for a page without snapshots. */
  snapshotId: string | null;
  protocolHash: string | null;
  retrievedAt: string;
  intendedUse: string;
}

/** One page found by search_evidence_pages. */
export interface McpPageMatch {
  slug: string;
  title: string;
  studyCount: number;
  updatedAt: string;
  url: string;
  snapshotId: string | null;
  protocolHash: string | null;
}

export interface McpSearchResult {
  query: string;
  pages: McpPageMatch[];
  /** True when more pages matched than were returned. */
  truncated: boolean;
  retrievedAt: string;
  intendedUse: string;
}

/** The pooled primary-outcome estimate stored with a snapshot, on the display scale. */
export interface ChangeEstimate {
  measure: string;
  estimate: number;
  ciLower: number;
  ciUpper: number;
}

export interface ChangedStudy {
  id: string;
  label: string;
}

/** One snapshot of the change log and what changed since the snapshot before it (F04, LIVE-082). */
export interface EvidenceChange {
  snapshotId: string;
  createdAt: string;
  /** Null for the page's first snapshot, which is compared with an empty page. */
  previousSnapshotId: string | null;
  note: string;
  studiesAdded: ChangedStudy[];
  studiesRemoved: ChangedStudy[];
  /** Null when that snapshot has no stored pooled result. */
  estimateBefore: ChangeEstimate | null;
  estimateAfter: ChangeEstimate | null;
  /** The estimate moved across no effect, or its interval started or stopped excluding it. */
  crossedNull: boolean;
  certainty: { from: string | null; to: string | null } | null;
  newlyRetracted: ChangedStudy[];
}

/** get_changes (PAPI-041): snapshots since a date, newest last. */
export interface EvidenceChangesResult extends McpProvenance {
  slug: string;
  title: string;
  /** YYYY-MM-DD, or null for the whole history. */
  since: string | null;
  changes: EvidenceChange[];
}
